import React, { useEffect, useState } from "react";
import { NavLink } from "react-router-dom";
import { Activity, Briefcase, Star, Radar, Building2, Newspaper, Moon, Sun, LogOut, User, BookOpen, LineChart } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import GlobalLookup from "@/components/GlobalLookup";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const links = [
  { to: "/", label: "Portfolio", icon: Briefcase, testid: "nav-portfolio", end: true },
  { to: "/watchlist", label: "Watchlist", icon: Star, testid: "nav-watchlist" },
  { to: "/movers", label: "Movers", icon: Radar, testid: "nav-movers" },
  { to: "/brokers", label: "Brokers", icon: Building2, testid: "nav-brokers" },
  { to: "/articles", label: "Articles", icon: Newspaper, testid: "nav-articles" },
  { to: "/practice", label: "Practice Trading", icon: LineChart, testid: "nav-practice" },
];

export default function Navbar() {
  const { user, logout, openAuth } = useAuth();
  const [dark, setDark] = useState(true);

  useEffect(() => {
    const stored = localStorage.getItem("stewart_theme");
    const isDark = stored ? stored === "dark" : true; // dark navy is the default experience
    setDark(isDark);
    document.documentElement.classList.toggle("dark", isDark);
  }, []);

  const toggleTheme = () => {
    const next = !dark;
    setDark(next);
    document.documentElement.classList.toggle("dark", next);
    localStorage.setItem("stewart_theme", next ? "dark" : "light");
  };

  return (
    <nav
      data-testid="main-navbar"
      className="sticky top-9 z-40 border-b border-border bg-background/80 backdrop-blur-xl"
    >
      <div className="max-w-[1500px] mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between gap-4">
        <div className="flex items-center gap-6 min-w-0">
          <NavLink to="/" data-testid="brand-logo" className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-lg bg-red-600 grid place-items-center shadow-lg shadow-red-600/30">
              <Activity className="w-5 h-5 text-primary-foreground" strokeWidth={2.5} />
            </div>
            <div className="leading-none">
              <div className="font-heading font-extrabold text-lg tracking-tight">STEWART CAP</div>
              <div className="text-[10px] font-num uppercase tracking-[0.25em] text-muted-foreground">FINANCE</div>
            </div>
          </NavLink>

          <a
            href="https://www.amazon.ca/Intelligent-Investor-Third-Investing-Financial/dp/0063497476?crid=2NM61SYO4WVXZ&dib_tag=se&keywords=the+best+investor&qid=1790023293&sr=8-1&linkCode=ll2&tag=stewartcap-20&linkId=e6b604904148f21939e6895e39e6a730&ref_=as_li_ss_tl"
            target="_blank"
            rel="noopener noreferrer sponsored"
            data-testid="affiliate-link"
            title="The Intelligent Investor — our recommended read"
            className="hidden xl:inline-flex items-center gap-1.5 rounded-full border border-red-500/30 bg-red-500/10 px-3 py-1.5 text-xs font-semibold text-red-400 hover:bg-red-500/20 hover:border-red-500/50 transition-colors"
          >
            <BookOpen className="w-3.5 h-3.5" /> Our Pick
          </a>

          <div className="hidden xl:flex items-center gap-1">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.end}
                data-testid={l.testid}
                className={({ isActive }) =>
                  `flex items-center gap-2 px-3.5 py-2 rounded-full text-sm font-medium transition-colors ${
                    isActive ? "bg-primary/15 text-primary" : "text-muted-foreground hover:text-foreground hover:bg-secondary/60"
                  }`
                }
              >
                <l.icon className="w-4 h-4" />
                {l.label}
              </NavLink>
            ))}
          </div>
        </div>

        <div className="flex items-center gap-2">
          <GlobalLookup />
          <Button
            variant="ghost"
            size="icon"
            onClick={toggleTheme}
            data-testid="theme-toggle"
            className="text-muted-foreground"
          >
            {dark ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
          </Button>

          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="secondary" className="gap-2" data-testid="user-menu-trigger">
                  <User className="w-4 h-4" />
                  <span className="hidden sm:inline max-w-[120px] truncate">{user.name}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel className="truncate">{user.email}</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={logout} data-testid="logout-btn" className="text-red-600">
                  <LogOut className="w-4 h-4 mr-2" /> Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <>
              <Button variant="ghost" onClick={() => openAuth("login")} data-testid="nav-login-btn" className="hidden sm:inline-flex">
                Sign in
              </Button>
              <Button onClick={() => openAuth("register")} data-testid="nav-register-btn">
                Get started
              </Button>
            </>
          )}
        </div>
      </div>

      <div className="xl:hidden flex items-center gap-1 px-3 pb-2 overflow-x-auto">
        {links.map((l) => (
          <NavLink
            key={l.to}
            to={l.to}
            end={l.end}
            className={({ isActive }) =>
              `flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap ${
                isActive ? "bg-secondary text-foreground" : "text-muted-foreground"
              }`
            }
          >
            <l.icon className="w-3.5 h-3.5" />
            {l.label}
          </NavLink>
        ))}
      </div>
    </nav>
  );
}
